import type {
  AdminCreateDropRequestInput,
  AdminDailyDropStats,
  AdminDropDto,
  AdminDropEntriesResponse,
  AdminUpdateDropRequest,
  DropWinnerAttachment,
  DropWinnersMessageRequest,
  DropWinnersMessageResponse,
} from '@pulsify/shared'
import { api, getAuthToken, unwrap } from '~/shared/lib/rpc'

interface DropLinkTarget {
  id: string
  name: string
}

const LINK_SEARCH_LIMIT = 8

export const dropsAdminApi = {
  async list(): Promise<AdminDropDto[]> {
    const res = await api.api.drops.admin.$get()
    const body = await unwrap<{ drops: AdminDropDto[] }>(res)
    return body.drops
  },

  async create(input: AdminCreateDropRequestInput): Promise<AdminDropDto> {
    const res = await api.api.drops.$post({ json: input })
    return (await unwrap<{ drop: AdminDropDto }>(res)).drop
  },

  async update(id: string, input: AdminUpdateDropRequest): Promise<AdminDropDto> {
    const res = await api.api.drops[':id'].$patch({ param: { id }, json: input })
    return (await unwrap<{ drop: AdminDropDto }>(res)).drop
  },

  async publish(id: string): Promise<AdminDropDto> {
    const res = await api.api.drops[':id'].publish.$post({ param: { id } })
    return (await unwrap<{ drop: AdminDropDto }>(res)).drop
  },

  async archive(id: string): Promise<AdminDropDto> {
    const res = await api.api.drops[':id'].archive.$post({ param: { id } })
    return (await unwrap<{ drop: AdminDropDto }>(res)).drop
  },

  async draw(id: string): Promise<AdminDropDto> {
    const res = await api.api.drops[':id'].draw.$post({ param: { id } })
    return (await unwrap<{ drop: AdminDropDto }>(res)).drop
  },

  async searchLinkTargets(kind: 'event' | 'listing', q: string): Promise<DropLinkTarget[]> {
    if (kind === 'event') {
      const res = await api.api.public.events.$get({ query: { q } })
      const body = await unwrap<{ items: { id: string; title: string }[] }>(res)
      return body.items.slice(0, LINK_SEARCH_LIMIT).map((item) => ({
        id: item.id,
        name: item.title,
      }))
    }
    const res = await api.api.public.locations.$get({ query: { q } })
    const body = await unwrap<{ items: { id: string; name: string }[] }>(res)
    return body.items.slice(0, LINK_SEARCH_LIMIT).map((item) => ({
      id: item.id,
      name: item.name,
    }))
  },

  async dailyStats(id: string): Promise<AdminDailyDropStats> {
    const res = await api.api.drops[':id']['daily-stats'].$get({ param: { id } })
    return unwrap<AdminDailyDropStats>(res)
  },

  async entries(id: string): Promise<AdminDropEntriesResponse> {
    const res = await api.api.drops[':id'].entries.$get({ param: { id } })
    return unwrap<AdminDropEntriesResponse>(res)
  },

  async messageWinner(id: string, userId: string, message: string): Promise<void> {
    await unwrap(
      await api.api.drops[':id'].winners[':userId'].message.$post({
        param: { id, userId },
        json: { message },
      }),
    )
  },

  async messageWinners(
    id: string,
    input: DropWinnersMessageRequest,
  ): Promise<DropWinnersMessageResponse> {
    const res = await api.api.drops[':id'].winners.message.$post({ param: { id }, json: input })
    return unwrap<DropWinnersMessageResponse>(res)
  },

  async uploadWinnerAttachment(file: File): Promise<DropWinnerAttachment> {
    const url = api.api.images['dm-file'].$url()
    const formData = new FormData()
    formData.append('file', file)
    const token = await getAuthToken()
    const res = await fetch(url, {
      method: 'POST',
      body: formData,
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
    return unwrap<DropWinnerAttachment>(res)
  },

  // The hero route reads the multipart body manually (images.ts pattern), so
  // the typed client only provides the URL; the request itself is a plain fetch.
  async uploadHero(id: string, file: File): Promise<{ heroImageUrl: string }> {
    const url = api.api.drops[':id'].hero.$url({ param: { id } })
    const formData = new FormData()
    formData.append('file', file)
    const token = await getAuthToken()
    const res = await fetch(url, {
      method: 'POST',
      body: formData,
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
    return unwrap<{ heroImageUrl: string }>(res)
  },
}
